import {
  Calendar,
  Download,
  FileText,
  FolderOpen,
  ImageIcon,
  Info,
  List,
  Palette,
  RotateCcw,
  Tag,
  Trash2,
  Workflow,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import { getItemTagName } from "@/modules/mine";
import type { Item } from "@/modules/mine/types";

import {
  formatBytes,
  formatDate,
  formatPercent,
  getImageExtension,
  getImageSizeText,
  getImageTitle,
  getOrientationLabel,
} from "./preview-utils";

const IMAGE_STAGE_PADDING_X = 96;
const IMAGE_STAGE_PADDING_Y = 160;
const LONG_IMAGE_ASPECT_RATIO = 2.4;

function getInitialImageScale({
  naturalHeight,
  naturalWidth,
  stageHeight,
  stageWidth,
}: {
  naturalHeight: number;
  naturalWidth: number;
  stageHeight: number;
  stageWidth: number;
}) {
  if (naturalHeight <= 0 || naturalWidth <= 0 || stageHeight <= 0 || stageWidth <= 0) {
    return 1;
  }

  const availableWidth = Math.max(1, stageWidth - IMAGE_STAGE_PADDING_X);
  const availableHeight = Math.max(1, stageHeight - IMAGE_STAGE_PADDING_Y);
  const widthScale = availableWidth / naturalWidth;
  const heightScale = availableHeight / naturalHeight;
  const isLongImage = naturalHeight / naturalWidth >= LONG_IMAGE_ASPECT_RATIO;
  const fitScale = isLongImage ? widthScale : Math.min(widthScale, heightScale);

  return Math.max(0.05, Math.min(1, fitScale));
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="flex items-center justify-between gap-5 text-xs leading-5">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right font-medium text-foreground/85">
        {value || "Unknown"}
      </span>
    </div>
  );
}

function InspectorBlock({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <section className={`border-b border-border/50 py-5 ${className}`}>{children}</section>;
}

function InspectorLabel({
  children,
  icon,
}: {
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center gap-2 text-xs font-medium text-muted-foreground">
      {icon}
      <span>{children}</span>
    </div>
  );
}

function MetadataPill({
  children,
  icon,
}: {
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <span className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-md bg-muted px-2 text-xs text-muted-foreground">
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

export function ImagePreview({
  externalUrl,
  folderName,
  item,
  onClose,
  onViewFlowRuns,
}: {
  externalUrl?: string;
  folderName: string;
  item: Item;
  onClose: () => void;
  onViewFlowRuns: () => void;
}) {
  const palettes = item.image?.visual?.palettes ?? [];
  const tags = item.tags ?? [];
  const stageRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const zoomPercentRef = useRef<HTMLSpanElement>(null);
  const [naturalSize, setNaturalSize] = useState({ height: 0, width: 0 });
  const [stageSize, setStageSize] = useState({ height: 0, width: 0 });
  const initialScale = useMemo(
    () =>
      getInitialImageScale({
        naturalHeight: naturalSize.height,
        naturalWidth: naturalSize.width,
        stageHeight: stageSize.height,
        stageWidth: stageSize.width,
      }),
    [naturalSize.height, naturalSize.width, stageSize.height, stageSize.width],
  );

  const updateZoomPercent = (scale: number) => {
    if (zoomPercentRef.current) {
      zoomPercentRef.current.textContent = `${Math.round(scale * 100)}%`;
    }
  };

  useEffect(() => {
    if (!stageRef.current) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }

      const nextHeight = Math.max(1, entry.contentRect.height);
      const nextWidth = Math.max(1, entry.contentRect.width);

      setStageSize((currentSize) => {
        if (currentSize.height === nextHeight && currentSize.width === nextWidth) {
          return currentSize;
        }

        return {
          height: nextHeight,
          width: nextWidth,
        };
      });
    });

    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setNaturalSize({ height: 0, width: 0 });
  }, [item.id, item?.url]);

  useEffect(() => {
    if (!naturalSize.height || !naturalSize.width || !transformRef.current) {
      return;
    }

    transformRef.current.centerView(initialScale, 0);
    updateZoomPercent(initialScale);
  }, [initialScale, item.id, naturalSize.height, naturalSize.width]);

  return (
    <div className="grid h-full min-h-0 bg-zinc-100 dark:bg-[#0f0f10] lg:grid-cols-[minmax(0,1fr)_320px]">
      <div ref={stageRef} className="group/image-stage relative min-h-0 overflow-hidden">
        <div className="pointer-events-auto absolute top-5 left-5 z-10 flex gap-2 opacity-0 transition-opacity duration-150 group-hover/image-stage:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            className="flex size-9 items-center justify-center rounded-lg border border-border/70 bg-background/90 text-muted-foreground hover:bg-background hover:text-foreground dark:bg-zinc-900/90 dark:hover:bg-zinc-800"
            title="下载图片"
            onClick={() => externalUrl && window.open(externalUrl, "_blank")}
          >
            <Download size={16} />
          </button>
          <button
            type="button"
            className="flex size-9 items-center justify-center rounded-lg border border-border/70 bg-background/90 text-muted-foreground hover:bg-background hover:text-destructive dark:bg-zinc-900/90 dark:hover:bg-zinc-800"
            title="删除"
          >
            <Trash2 size={16} />
          </button>
        </div>

        <TransformWrapper
          key={item.id}
          centerOnInit
          // centerZoomedOut
          limitToBounds
          initialScale={initialScale}
          minScale={0.05}
          maxScale={6}
          smooth={false}
          wheel={{ step: 0.048 }}
          doubleClick={{ animationTime: 180, mode: "toggle", step: 0.6 }}
          panning={{ velocityDisabled: false }}
          zoomAnimation={{ animationTime: 180, size: 0.25 }}
          onInit={(ref) => {
            transformRef.current = ref;
            updateZoomPercent(ref.state.scale);
          }}
          onTransform={(ref) => updateZoomPercent(ref.state.scale)}
          customTransform={(x, y, scale) =>
            `translate3d(${x}px, ${y}px, 0) scale(${Number.parseFloat(scale.toFixed(8))})`
          }
        >
          {({ zoomIn, zoomOut }) => {
            return (
              <>
                <TransformComponent
                  wrapperClass="!h-full !w-full image-preview-checkerboard"
                  contentClass="cursor-grab active:cursor-grabbing will-change-transform"
                >
                  {item?.url ? (
                    <img
                      src={item?.url}
                      alt={getImageTitle(item)}
                      className="block max-w-none select-none rounded-[24px] object-contain shadow-sm"
                      draggable={false}
                      style={
                        naturalSize.width > 0 && naturalSize.height > 0
                          ? {
                              height: naturalSize.height,
                              width: naturalSize.width,
                            }
                          : undefined
                      }
                      onLoad={(event) => {
                        setNaturalSize({
                          height: event.currentTarget.naturalHeight,
                          width: event.currentTarget.naturalWidth,
                        });
                      }}
                    />
                  ) : (
                    <div className="flex size-64 flex-col items-center justify-center gap-3 text-muted-foreground">
                      <ImageIcon size={36} strokeWidth={1.5} />
                      <span className="text-sm">图片暂时不可用</span>
                    </div>
                  )}
                </TransformComponent>

                <div className="pointer-events-auto absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center overflow-hidden rounded-lg border border-border/70 bg-background/95 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover/image-stage:opacity-100 focus-within:opacity-100 dark:bg-zinc-900/95">
                  <button
                    type="button"
                    className="flex size-9 items-center justify-center hover:bg-muted hover:text-foreground"
                    title="缩小"
                    onClick={() => zoomOut(0.2)}
                  >
                    <ZoomOut size={15} />
                  </button>
                  <div
                    className="flex h-9 min-w-16 items-center justify-center border-x border-border/70 px-4 text-center text-xs font-medium text-foreground/80 hover:bg-muted"
                    title="当前缩放比例"
                  >
                    <span ref={zoomPercentRef}>100%</span>
                  </div>
                  <button
                    type="button"
                    className="flex size-9 items-center justify-center hover:bg-muted hover:text-foreground"
                    title="放大"
                    onClick={() => zoomIn(0.2)}
                  >
                    <ZoomIn size={15} />
                  </button>
                  <button
                    type="button"
                    className="flex size-9 items-center justify-center border-l border-border/70 hover:bg-muted hover:text-foreground"
                    title="重置"
                    onClick={() => transformRef.current?.centerView(initialScale, 180)}
                  >
                    <RotateCcw size={14} />
                  </button>
                </div>
              </>
            );
          }}
        </TransformWrapper>
      </div>

      <aside className="flex min-h-0 flex-col border-t border-border/60 bg-background lg:border-l lg:border-t-0">
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-border/50 px-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-2">
            <Info size={15} />
            详细信息
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-md hover:bg-muted hover:text-foreground"
              title="流程记录"
              onClick={onViewFlowRuns}
            >
              <Workflow size={16} />
            </button>
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-md hover:bg-muted hover:text-foreground"
              title="关闭预览"
              onClick={onClose}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          <InspectorBlock className="pt-6">
            <div className="min-w-0">
              <h2 className="text-base font-semibold leading-6 text-foreground">
                {getImageTitle(item)}
              </h2>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={14} />
                  {formatDate(item.created_at)}
                </span>
                <span aria-hidden="true">•</span>
                <MetadataPill icon={<FolderOpen className="size-3.5" />}>
                  {folderName || "未分类"}
                </MetadataPill>
              </div>
            </div>
          </InspectorBlock>

          <InspectorBlock>
            <InspectorLabel icon={<List size={14} />}>基础信息</InspectorLabel>
            <div className="space-y-4">
              <InfoRow label="分辨率" value={getImageSizeText(item)} />
              <InfoRow label="格式" value={getImageExtension(item)} />
              <InfoRow label="大小" value={formatBytes(Number(item?.size || 0))} />
              <InfoRow label="朝向" value={getOrientationLabel(item)} />
            </div>
          </InspectorBlock>

          <InspectorBlock>
            <InspectorLabel icon={<Palette size={14} />}>色彩</InspectorLabel>
            {palettes.length > 0 ? (
              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-xs leading-5">
                  <span className="text-muted-foreground">主色数量</span>
                  <span className="font-medium text-foreground/85">
                    {palettes.length} 个主色
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {palettes.map((color) => (
                    <div
                      key={color.hex}
                      className="h-5 flex-1 rounded-sm border border-border/70"
                      style={{ backgroundColor: color.hex }}
                      title={`${color.hex} · ${formatPercent(color.coverage)}`}
                    />
                  ))}
                </div>
                <div className="space-y-1.5">
                  {palettes.map((color) => (
                    <div
                      key={`${color.hex}-meta`}
                      className="flex items-center justify-between gap-4 text-xs"
                    >
                      <span className="flex items-center gap-2 text-foreground/80">
                        <span
                          className="size-2 rounded-full border border-border/70"
                          style={{ backgroundColor: color.hex }}
                        />
                        {color.hex}
                      </span>
                      <span className="text-muted-foreground">
                        {formatPercent(color.coverage)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                暂无色彩信息
              </div>
            )}
          </InspectorBlock>

          <InspectorBlock>
            <InspectorLabel icon={<Tag size={14} />}>标签</InspectorLabel>
            {tags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <MetadataPill key={tag.id}>#{getItemTagName(tag)}</MetadataPill>
                ))}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">暂无标签</span>
            )}
          </InspectorBlock>

          <InspectorBlock className="border-b-0">
            <InspectorLabel icon={<FileText size={14} />}>备注</InspectorLabel>
            <p className="whitespace-pre-wrap text-xs leading-5 text-foreground/80">
              {item?.note || "暂无备注"}
            </p>
          </InspectorBlock>
        </div>
      </aside>
    </div>
  );
}
